/**
 * The fixture's stand-in for the backend automation engine: runs a version's
 * global rules after a card has moved. Pure — it returns what to apply and
 * leaves the store to the caller.
 */
import {
  AutomationActionKind,
  AutomationAfterExecution,
  AutomationCondition,
  AutomationConditionKind,
  AutomationRule,
  AutomationTrigger,
  AutomationTriggerKind,
  StepFormFieldType,
  StepFormValue,
  StepFormValues,
  WorkflowVersion,
} from '../types';

/** The move a person made, as the rules see it. */
export interface AutomationMove {
  fromStatusKey: string;
  toStatusKey: string;
  /** Everything the card has filled in, keyed by the step form's status. */
  stepFormValues: Record<string, StepFormValues>;
  /** `YYYY-MM-DD`, for the rules' validity period. */
  today: string;
}

export interface AutomationResult {
  /** Where the card ends up; equals `move.toStatusKey` when no rule moved it. */
  statusKey: string;
  /** Each status an action moved the card to, in order. */
  moves: { fromStatusKey: string; toStatusKey: string }[];
  /** The rules that fired, in run order. */
  firedRuleIds: string[];
}

/**
 * The status payload carries no transition key, so the fixture resolves it the
 * way the board offered the drop: a transition out of the exact status wins
 * over a global one.
 */
export const resolveTransitionKey = (
  version: WorkflowVersion,
  fromStatusKey: string,
  toStatusKey: string,
) => {
  const into = version.transitions.filter(
    transition => transition.toStatusKey === toStatusKey,
  );

  return (
    into.find(transition => transition.fromStatusKey === fromStatusKey)?.key ??
    into.find(transition => transition.fromStatusKey === null)?.key ??
    null
  );
};

const isActive = (rule: AutomationRule, today: string) =>
  rule.isEnabled &&
  (!rule.activeFrom || rule.activeFrom.slice(0, 10) <= today) &&
  (!rule.activeTo || rule.activeTo.slice(0, 10) >= today);

const isTriggered = (
  trigger: AutomationTrigger,
  move: AutomationMove,
  transitionKey: string | null,
) => {
  if (trigger.kind === AutomationTriggerKind.StatusEntered) {
    return trigger.statusKey === move.toStatusKey;
  }

  return (
    transitionKey !== null &&
    (trigger.transitionKey === null || trigger.transitionKey === transitionKey)
  );
};

/** The stored value as a condition compares it — see `StepFormValueCondition`. */
const toComparable = (
  value: StepFormValue | undefined,
  type: StepFormFieldType | undefined,
): string[] => {
  if (value === undefined || value === null) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'boolean') {
    if (type === StepFormFieldType.YesNo) return [value ? 'yes' : 'no'];
    return [String(value)];
  }
  if (typeof value === 'object') return [];

  return [String(value)];
};

const holds = (
  condition: AutomationCondition,
  move: AutomationMove,
  transitionKey: string | null,
  version: WorkflowVersion,
) => {
  switch (condition.kind) {
    case AutomationConditionKind.PreviousStatusIn:
      return condition.keys.includes(move.fromStatusKey);
    case AutomationConditionKind.PreviousStatusNotIn:
      return !condition.keys.includes(move.fromStatusKey);
    case AutomationConditionKind.TransitionIs:
      return transitionKey !== null && condition.keys.includes(transitionKey);
    case AutomationConditionKind.StepFormValueEquals: {
      const field = version.statuses
        .find(status => status.key === condition.statusKey)
        ?.stepForm?.fields.find(item => item.key === condition.fieldKey);

      return toComparable(
        move.stepFormValues[condition.statusKey]?.[condition.fieldKey],
        field?.type,
      ).includes(condition.value);
    }
  }
};

/**
 * Runs the rules top to bottom against the move. Every rule sees the move the
 * person made, not where an earlier rule left the card, and a rule's own
 * `MoveToStatus` does not start the rules over — a backend would need a loop
 * guard for that, the fixture simply does not cascade.
 */
export const runAutomationRules = (
  rules: AutomationRule[],
  version: WorkflowVersion,
  move: AutomationMove,
): AutomationResult => {
  const transitionKey = resolveTransitionKey(
    version,
    move.fromStatusKey,
    move.toStatusKey,
  );
  const result: AutomationResult = {
    statusKey: move.toStatusKey,
    moves: [],
    firedRuleIds: [],
  };

  for (const rule of rules) {
    if (
      !isActive(rule, move.today) ||
      !isTriggered(rule.trigger, move, transitionKey) ||
      !rule.conditions.every(condition =>
        holds(condition, move, transitionKey, version),
      )
    ) {
      continue;
    }

    result.firedRuleIds.push(rule.id);

    let stop = false;

    for (const action of rule.actions) {
      if (
        action.kind === AutomationActionKind.MoveToStatus &&
        action.statusKey &&
        action.statusKey !== result.statusKey
      ) {
        result.moves.push({
          fromStatusKey: result.statusKey,
          toStatusKey: action.statusKey,
        });
        result.statusKey = action.statusKey;
      }

      // Email, notification and the assignee have nothing to act on in the
      // fixture: it sends no mail and its cards carry no assignee.

      if (action.afterExecution === AutomationAfterExecution.StopOtherRules) {
        stop = true;
      }
    }

    if (stop) break;
  }

  return result;
};
